import React, { useState, useEffect } from 'react';
import { Image, LoadingOverlay } from '@mantine/core';

// Utility function to preload an image
export const preloadImage = (src: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (!src) {
            reject(new Error('No image source provided'));
            return;
        }

        const img = new globalThis.Image();
        img.onload = () => resolve(src);
        img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        img.src = src;
    });
};

// Interface for AsyncImage props
interface AsyncImageProps extends Omit<React.ComponentProps<typeof Image>, 'src'> {
    src: string | undefined;
    fallbackSrc?: string;
    loadingHeight?: number;
    alt?: string;
    fit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    width?: number | string;
    height?: number | string;
    radius?: string;
    style?: React.CSSProperties;
}

/**
 * AsyncImage component that handles asynchronous image loading with loading states and error handling
 * 
 * @param src - The source URL of the image to load
 * @param fallbackSrc - A fallback image to show if loading fails
 * @param loadingHeight - Height to use during the loading state
 * @param props - Any other props to pass to the underlying Image component
 */
export const AsyncImage = ({
    src,
    fallbackSrc = '/placeholder.png',
    loadingHeight = 150,
    ...props
}: AsyncImageProps) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);

    useEffect(() => {
        // Reset states when source changes
        setLoaded(false);
        setError(false);

        if (!src) {
            setError(true);
            return;
        }

        let isMounted = true;

        preloadImage(src)
            .then((resolvedSrc) => {
                if (isMounted) {
                    setImageSrc(resolvedSrc);
                    setLoaded(true);
                }
            })
            .catch((err) => {
                console.error(err.message);
                if (isMounted) {
                    setError(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [src]);

    if (error) {
        return <Image {...props} src={fallbackSrc} />;
    }

    if (!loaded) {
        return (
            <div style={{
                position: 'relative',
                height: loadingHeight ||
                    (typeof props.height === 'number' ? props.height : 150)
            }}>
                <LoadingOverlay visible={true} />
            </div>
        );
    }

    return <Image {...props} src={imageSrc} />;
}; 